import * as THREE from 'three';

/**
 * Engine owns the WebGL device, the render targets and the frame budget.
 * Everything else in the game receives it and never touches the renderer directly.
 */
export class Engine {
  constructor(container) {
    this.container = container;

    this.renderer = new THREE.WebGLRenderer({
      antialias: false, // resolved by the post stack (SMAA/TAA)
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
    });
    /**
     * Resolution multiplier from the quality preset.
     *
     * Applied on top of the device pixel ratio rather than instead of it, so a
     * high-DPI screen still gets a sharper image than a low-DPI one at the same
     * setting. Cost is quadratic in this number, which is why it is the first
     * lever every preset reaches for.
     */
    this.renderScale = 1;
    this.renderer.setPixelRatio(this._pixelRatio());
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.shadowMap.autoUpdate = true;
    this.renderer.info.autoReset = false;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      80, container.clientWidth / container.clientHeight, 0.02, 600
    );
    this.camera.rotation.order = 'YXZ';

    // Viewmodel is rendered by a second camera with a tight FOV so hands/weapon
    // never clip into world geometry — the standard FPS two-pass trick.
    this.viewmodelScene = new THREE.Scene();
    this.viewmodelCamera = new THREE.PerspectiveCamera(
      60, container.clientWidth / container.clientHeight, 0.008, 12
    );
    this.viewmodelCamera.rotation.order = 'YXZ';

    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.pmrem.compileEquirectangularShader();

    this.clock = new THREE.Clock();
    this.elapsed = 0;

    this._onResize = this.resize.bind(this);
    addEventListener('resize', this._onResize);
  }

  _pixelRatio() {
    return Math.min(devicePixelRatio, 2) * this.renderScale;
  }

  /**
   * Change the resolution the world is rendered at. 1 is native.
   *
   * Everything downstream is sized from the renderer, so this goes through the
   * same `resize` path a window change does — the composer, the post targets
   * and the HUD all follow rather than being resized by hand here.
   */
  setRenderScale(scale) {
    const next = Math.max(0.3, Math.min(1, scale));
    if (Math.abs(next - this.renderScale) < 1e-4) return;
    this.renderScale = next;
    this.resize();
  }

  get size() {
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
      dpr: this.renderer.getPixelRatio(),
    };
  }

  resize() {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setPixelRatio(this._pixelRatio());
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.viewmodelCamera.aspect = w / h;
    this.viewmodelCamera.updateProjectionMatrix();
    this.onResize?.(w, h);
  }

  dispose() {
    removeEventListener('resize', this._onResize);
    this.pmrem.dispose();
    this.renderer.dispose();
  }
}
